import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LeaderboardUser } from '@/types/leaderboard';

interface LeaderboardTableProps {
  users: LeaderboardUser[];
}

const LeaderboardTable: React.FC<LeaderboardTableProps> = ({ users }) => {
  const navigate = useNavigate();
  
  if (!users || users.length === 0) {
    return (
      <div className="vibebops-card mb-6 p-8 text-center">
        <p className="text-vibebops-textMuted">No data available for this time period.</p>
        <p className="text-sm mt-2">Check back later as more users engage with the platform!</p>
      </div>
    );
  }

  const handleProfileClick = (userId: number | string) => {
    navigate(`/profile/${userId}`);
  };

  return (
    <div className="vibebops-card mb-6">
      <div className="flex items-center mb-6 px-3">
        <div className="w-10 text-center font-bold">#</div>
        <div className="flex-1 ml-3 font-bold">User</div>
        <div className="w-20 text-center font-bold">Level</div>
        <div className="w-20 text-center font-bold">Score</div>
        <div className="w-20 text-center font-bold">Change</div>
      </div>
      
      <div className="space-y-2">
        {users.map((user, index) => (
          <div 
            key={user.id} 
            className="flex items-center p-3 rounded-lg hover:bg-vibebops-highlight cursor-pointer transition-colors"
            onClick={() => handleProfileClick(user.id)}
          >
            <div className="w-10 text-center font-medium">
              {index + 1 === 1 && '🥇'}
              {index + 1 === 2 && '🥈'}
              {index + 1 === 3 && '🥉'}
              {index + 1 > 3 && index + 1}
            </div>
            <div className="flex items-center flex-1 ml-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 mr-3 overflow-hidden hover:ring-2 hover:ring-vibebops-primary transition-all">
                <img 
                  src={user.avatar || '/placeholder.svg'} 
                  alt={user.name} 
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/placeholder.svg';
                  }}
                />
              </div>
              <div>
                <p className="font-medium group-hover:text-vibebops-primary transition-colors">{user.name || 'Anonymous User'}</p>
                <p className="text-xs text-vibebops-textMuted">{user.username || '@unknown'}</p>
              </div>
            </div>
            <div className="w-20 text-center">
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-vibebops-accent/20 text-vibebops-accent">
                Lvl {user.level || 1}
              </span>
            </div>
            <div className="w-20 text-center font-medium">{user.score || 0}</div>
            <div className="w-20 text-center">
              <span className={`text-xs ${user.change > 0 ? 'text-green-500' : user.change < 0 ? 'text-red-500' : 'text-vibebops-textMuted'}`}>
                {user.change > 0 ? `↑ ${user.change}` : user.change < 0 ? `↓ ${Math.abs(user.change)}` : '-'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LeaderboardTable;
